import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, Receipt } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { fetchStatus, openPortalFromCheckout } from "../lib/useCheckout";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX = 20; // ~40s

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", payment_status: "pending" });
  const [pollCount, setPollCount] = useState(0);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const timerRef = useRef(null);

  const handleOpenPortal = async () => {
    if (!sessionId) return;
    setPortalLoading(true);
    setPortalError(null);
    try {
      await openPortalFromCheckout(sessionId);
    } catch (e) {
      setPortalError(e.message || "Could not open portal");
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Payment confirmed — Gadget Guy Media";
    setLink("canonical", "https://gadgetguymedia.com/checkout/success");
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "unknown" });
      return;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const data = await fetchStatus(sessionId);
        if (cancelled) return;
        setState(data);
        setPollCount((c) => c + 1);
        // stop when paid, failed, or max reached
        if (
          data.payment_status === "paid" ||
          data.payment_status === "failed" ||
          data.status === "expired"
        ) {
          return;
        }
        if (pollCount + 1 >= POLL_MAX) return;
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", payment_status: "unknown" });
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = state.payment_status === "failed" || state.status === "expired";
  const stillPending = !paid && !failed;

  return (
    <PageShell testId="checkout-success-page">
      <section
        className="relative"
        style={{ paddingTop: "clamp(48px, 12vh, 128px)", paddingBottom: "clamp(48px, 8vh, 96px)" }}
      >
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          {paid ? (
            <CheckCircle2
              size={72}
              className="text-[var(--ggm-signal-cyan)] mx-auto"
              data-testid="checkout-paid-icon"
            />
          ) : failed ? (
            <div
              className="mx-auto w-[72px] h-[72px] rounded-full border-2 border-red-400 flex items-center justify-center text-red-300 text-3xl"
              data-testid="checkout-failed-icon"
            >
              !
            </div>
          ) : (
            <Loader2
              size={72}
              className="text-white/70 mx-auto animate-spin"
              data-testid="checkout-pending-icon"
            />
          )}

          <h1
            className="mt-6 font-condensed uppercase text-white"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", letterSpacing: "-0.015em" }}
            data-testid="checkout-heading"
          >
            {paid ? "Payment confirmed." : failed ? "Something went wrong." : "Confirming your payment…"}
          </h1>

          <p
            className="mt-5 font-body text-white/75 text-base md:text-lg leading-relaxed"
            data-testid="checkout-copy"
          >
            {paid ? (
              <>
                Thanks — I'll be in touch shortly to schedule your session. A Stripe receipt is on its way to
                your email, and I've also sent a confirmation from Gadget Guy Media.
              </>
            ) : failed ? (
              <>
                Your payment didn't complete. No charge was captured. Head back to pricing and try again, or
                reach out and I'll help sort it.
              </>
            ) : (
              <>
                Hang tight — we're waiting for Stripe to confirm the payment. This usually takes a few seconds.
              </>
            )}
          </p>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            {paid && (
              <button
                type="button"
                onClick={handleOpenPortal}
                disabled={portalLoading}
                className="ggm-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="checkout-cta-portal"
              >
                {portalLoading ? "Opening…" : "Manage billing"} <Receipt size={16} />
              </button>
            )}
            <Link to="/" className={paid ? "ggm-btn-secondary" : "ggm-btn-primary"} data-testid="checkout-cta-home">
              Back home <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" className="ggm-btn-secondary" data-testid="checkout-cta-pricing">
              Back to pricing <ArrowRight size={16} />
            </Link>
          </div>

          {portalError && (
            <p className="mt-4 font-body text-red-300 text-sm" data-testid="checkout-portal-error">
              {portalError}
            </p>
          )}

          {stillPending && pollCount >= POLL_MAX && (
            <p className="mt-6 font-body text-white/50 text-sm" data-testid="checkout-pending-note">
              Still confirming — you'll receive an email as soon as it's cleared. You can safely close this
              tab.
            </p>
          )}

          {sessionId && (
            <p className="mt-10 font-mono text-[11px] text-white/30" data-testid="checkout-session-id">
              Ref: {sessionId}
            </p>
          )}
        </div>
      </section>
    </PageShell>
  );
}
